/**
 * Lightweight frontend error capture helper.
 * Centralizes Sentry reporting tags so critical UI flows stay consistent.
 */

export interface FrontendErrorContext {
	area: string;
	action: string;
	[key: string]: string | number | boolean | undefined;
}

function normalizeError(error: unknown): Error {
	if (error instanceof Error) {
		return error;
	}
	if (typeof error === "string") {
		return new Error(error);
	}
	try {
		return new Error(JSON.stringify(error));
	} catch {
		return new Error("Unknown frontend error");
	}
}

export function captureFrontendError(error: unknown, context: FrontendErrorContext) {
	const normalized = normalizeError(error);

	if (import.meta.env.DEV) {
		console.error(`[Frontend Error] ${context.area}:${context.action}`, normalized);
	}

	import("@sentry/tanstackstart-react")
		.then((Sentry) => {
			Sentry.captureException(normalized, {
				tags: {
					area: context.area,
					action: context.action,
				},
				extra: context,
			});
		})
		.catch((captureError) => {
			if (import.meta.env.DEV) {
				console.error("[Frontend Error] Failed to send to Sentry", captureError);
			}
		});
}
